import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ApiResponse, BaseApiService } from '../../api/base';
import { Company } from '@models/company';
import { OperationResult } from '@models/operation-result';
import { AuthService } from '@core/auth/auth.service';
import { DeleteUser, StatusOptions } from '@models/forms';
import { DcInteractionTypeID, DcTypeofEmailCommunication, Interaction, InteractionType, Investor, StatusId, WorkFlowStatusId } from '@models/auxiliary-company-models';
import { VotingStats } from '@models/voting';
import { User } from '@models/user';
import { Nullable } from '@models/nullable';
import { HistoryRecord } from '@models/company-history';
import { Tag } from '@models/tag';
import { PodInteraction, PodStage } from '@core/models/pod-filter';
import { ChartData } from '@core/components/interaction-scatter-chart/interaction-scatter-chart.component';

export enum DCCompanyType {
  //Undefined = 0,
  "Lender" = 1,
  "Limited Partner" = 2,
  "VC / PE Investor" = 3,
  "Portfolio Company" = 4,
  "Intermediary" = 5,
  "Target" = 6
}

@Injectable({
  providedIn: 'root'
})
export class CompaniesService extends BaseApiService<Company> {

  constructor(authService: AuthService, router: Router, messageService: MessageService) {
    super('/companies', authService, router, messageService);
  }

  getCompanyDetails(id: number): Observable<ApiResponse<Company>> {
    return this.get(`/${id}/details`);
  }

  getCompanyByDomain(domain: string): Observable<ApiResponse<Company>> {
    return this.get(`/v2/domain/${domain}`);
  }

  getVotingStats(id: number): Observable<ApiResponse<VotingStats>> {
    return this.get(`/workflows/${id}/votes`);
  }

  getCompanyHistory(id: number): Observable<ApiResponse<HistoryRecord[]>> {
    return this.get(`/${id}/history`);
  }

  moveToPipeline(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/assign`);
  }

  archive(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/archivate`);
  }

  activate(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/activate`);
  }

  moveToPriority(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/prioritize`);
  }

  moveFromPriority(id: number, comment: Nullable<string>): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/deprioritize`, { comment: comment ?? undefined });
  }

  changeStatus(id: number, statusId: StatusId): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/status`, { status: statusId });
  }

  getStatuses(): Observable<ApiResponse<StatusOptions[]>> {
    return this.get('/statuses');
  }

  editCoveragePersons(id: number, assignees: User[], comment: Nullable<string>): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/assignees`, { comment: comment ?? undefined }, assignees.map(({ id }) => id));
  }

  interact(id: number, interactionType: InteractionType, summary: string, details: string, interactionDate: string): Observable<ApiResponse<OperationResult>> {
    return this.post('/interact', null, {
      companyId: id,
      interactionType,
      summary,
      details,
      interactionDate
    });
  }

  getInteractions(id: number): Observable<ApiResponse<OperationResult<Interaction>>> {
    return this.get(`/${id}/interactions`);
  }

  deleteUser(data: DeleteUser): Observable<ApiResponse<OperationResult>> {
    return this.delete('/people/' + data.id + '/' + data.deleteFromSystem);
  }

  enrichData(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/enrich`);
  }

  updateEnrichmentStatus(id: number, data: { status: WorkFlowStatusId, assigneeId?: number }): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/enrich/status`, data);
  }

  flagForICVoting(id: number, description: string, checkSize: number, users: Nullable<User[]>): Observable<ApiResponse<OperationResult>> {
    let url = `/${id}/review`;
    
    if (users && users.length > 0) {
      const queryParams = users.map(user => `userIds=${user.id}`).join('&');
      url = `${url}?${queryParams}`;
    }
    return this.post(url, { description: description ?? undefined, checkSize });
  }

  voteForCompany(id: number, vote: number, comment: string): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/votes`, { vote, comment });
  }

  removeFromIC(id: number): Observable<ApiResponse<OperationResult>> {
    return this.delete(`/${id}/review`);
  }

  moveCompanyFromActiveICtoHistory(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/review/complete`);
  }

  editICAsk(id: number, description: string): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/review/description`, { description: description });
  }

  releaseCompany(id: number): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/release`);
  }

  getTags(id: number, query: string): Observable<ApiResponse<Tag[]>> {
    //back slashes and other characters are possible
    return this.get(`/${id}/tags`, { term: query });
  }

  createTag(tag: Tag): Observable<ApiResponse<OperationResult>> {
    return this.post(`/tags`, null, tag);
  }

  updateTag(tag: Tag): Observable<ApiResponse<OperationResult>> {
    return this.put(`/tags`, null, tag);
  }

  createTagForSingleCompany(id: number, tag: Tag): Observable<ApiResponse<OperationResult>> {
    return this.post(`/${id}/tags`, null, tag);
  }

  updateTagForSingleCompany(id: number, tag: Tag): Observable<ApiResponse<OperationResult>> {
    return this.put(`/${id}/tags`, null, tag);
  }

  removeTag(id: number, { id: tagId }: Tag): Observable<ApiResponse<OperationResult>> {
    return this.delete(`/${id}/tags/${tagId}`);
  }

  updateICDateAdded(companyWorkFlowId: number, iCDateTime: string): Observable<ApiResponse<OperationResult>> {
    return this.put(`/IcVotingDate?companyWorkFlowId=${companyWorkFlowId}&iCDateTime=${iCDateTime}`, null);
  }
  updateOutReachOwner(companyId: number, email?: string): Observable<ApiResponse<OperationResult>> {
    return this.put(`/${companyId}/outreachowner`, null, { email });
  }

  updateNextStep(companyId: number, nextStep?: string): Observable<ApiResponse<OperationResult>> {
    return this.put(`/${companyId}/nextstep`, null, { nextStep });
  }

  updateRoundSummary(workflowId: number, roundId: number, summary: string) {
    return this.patch(`/workflows/${workflowId}/rounds/${roundId}/update-round-summary`, null, { roundSummary: summary});
  }

  getCompanyStages(assigneesInclude: number[] | undefined): Observable<ApiResponse<PodStage>> {
    let url = '/stats';
    
    if (assigneesInclude && assigneesInclude.length > 0) {
      const queryParams = assigneesInclude.map(assignee => `assigneesInclude=${assignee}`).join('&');
      url = `${url}?${queryParams}`;
    }
  
    return this.get(url);
  }

  getCompanyInteraction(
    interactionInitiatorsInclude: number[] | undefined,
    dcCompanyTypes?: DCCompanyType[]
  ): Observable<ApiResponse<PodInteraction>> {
    let url = '/interaction/stats';
    const queryParams: string[] = [];
    
    if (!this.authService.isPartner){
      interactionInitiatorsInclude = [this.authService.userId!];
    }

    // Add interaction initiators to query parameters if present
    if (interactionInitiatorsInclude && interactionInitiatorsInclude.length > 0) {
      const initiatorsQuery = interactionInitiatorsInclude.map(interaction => `interactionInitiatorsInclude=${interaction}`).join('&');
      queryParams.push(initiatorsQuery);
    }
  
    // Add company types to query parameters if present
    if (dcCompanyTypes && dcCompanyTypes.length > 0) {
      const companyTypesQuery = dcCompanyTypes.map(type => `dcCompanyTypes=${type}`).join('&');
      queryParams.push(companyTypesQuery);
    }
  
    // Construct the final URL with query parameters
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
  
    return this.get(url);
  }

  updateInvestorsDetails(companyId: number, investorsDetails?:  Nullable<string>, previousInvestorsDetails?:  Nullable<string>){
    return this.patch(`/${companyId}/investors-details`, null, {investorsDetails: investorsDetails, previousInvestorsDetails: previousInvestorsDetails})
  }

  deleteVote(companyId: number, voteId: number): Observable<ApiResponse<any>>{
    return this.post(`/${companyId}/reset-vote/${voteId}`)
  }

  getInteractionChart(interactionType: number[] | undefined, days: number | undefined, interactionInitiatorsInclude: number[] | undefined, dCCompanyTypes?: any): Observable<ApiResponse<ChartData[]>> {
    if (!this.authService.isPartner){
      interactionInitiatorsInclude = [this.authService.userId!];
    }
    return this.post(`/interaction/graph-data`, null, 
      {
      interactionTypes: interactionType, 
      days: days, 
      interactionInitiatorsInclude: interactionInitiatorsInclude,
      dCCompanyTypes: dCCompanyTypes
      }
      );
  }

  updateTotalPIC(companyId: number, totalPIC?:  Nullable<string>, previousTotalPIC?:  Nullable<string>){
    return this.patch(`/${companyId}/totalpic`, null, {totalPIC: totalPIC, previousTotalPIC: previousTotalPIC});
  }

  updateFinancials(companyId: number, targetFinancials?:  Nullable<string>, previousTargetFinancials ?:  Nullable<string>){
    return this.patch(`/${companyId}/target-financials`, null, {financialDetails: targetFinancials, previousFinancialDetails : previousTargetFinancials});
  }

  updateHC(companyId: number, hC1Y2Y?:  Nullable<string>, previousHC1Y2Y?: Nullable<string>){
    return this.patch(`/${companyId}/HC1Y2Y`, null, {HC1Y2Y  : hC1Y2Y, PreviousHC1Y2Y  : previousHC1Y2Y});
  }

  updateBackupCoverage(companyId: number, primaryEmail?:  string, secondaryEmail?: string): Observable<ApiResponse<OperationResult>>{
    return this.put(`/${companyId}/outreachBackups`, null, {PrimaryBackupEmail : primaryEmail, SecondaryBackupEmail : secondaryEmail});
  }

  addNote(companyId: number, title?:  string, note?: string): Observable<ApiResponse<OperationResult>>{
    return this.post(`/${companyId}/notes`, null, {title : title, note: note});
  }

  updateElevationOwner(companyId: number, email?: string): Observable<ApiResponse<OperationResult>> {
    return this.put(`/${companyId}/elevationOwner`, null, { email });
  }

  getCompanyInvestors(): Observable<ApiResponse<OperationResult>>{
    return this.get("/investors");
  }

  updateInvestors(companyId: number, investors: Investor[]){
    return this.patch(`/${companyId}/investors`, null, {InvestorIds: investors.map(investor => investor.id)});
  }

  addDcInteraction(companyId: number, dcInteractionType?: DcInteractionTypeID, title?:  string, note?: string, dcTypeOfEmailCommunication?: DcTypeofEmailCommunication, from?: string, to?: string[], cc?: string[], others?: string[], internalRecipients?: User[], date?: Date, body?: string): Observable<ApiResponse<OperationResult>>{
    console.log(to);
    return this.post(`/${companyId}/interaction`, null, {
      title: title,
      note: dcInteractionType == DcInteractionTypeID.Email ? (body ?? "") : (note ?? ""),
      dcInteractionType: dcInteractionType,
      dcTypeOfEmailCommunication: dcInteractionType == DcInteractionTypeID.Email ? dcTypeOfEmailCommunication : null,
      recipients: dcInteractionType == DcInteractionTypeID.Email ? {
        from: from,
        to: to,
        cc: cc,         
        other: others,
      } : null, 
      internalRecipients: dcInteractionType == DcInteractionTypeID.Email ? internalRecipients?.map(user => user.id) : null,
      date: date ? date.toISOString() : null
    })
  }


}